/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

const MFA_CONTACTS_STORAGE_KEY = "@UltragazEmpresarial:MFAContacts";

interface ContactDTO {
  contactId: string;
  contact: string;
  contactType: "EMAIL" | "PHONE";
  isContactValidated: boolean;
  name: string;
  siteId: string;
  contactIdSf: string;
  isNeedUpdate: boolean;
}

interface QuestionDTO {
  id: string;
  question: string;
}

interface CredentialsDTO {
  login: string;
  password: string;
  token: string | null;
}

interface MFADTO {
  credentials: CredentialsDTO;
  setCredentials: Dispatch<SetStateAction<CredentialsDTO>>;
  setAttempts: Dispatch<SetStateAction<number>>;
  attempts: number;
  userId: string | null;
  setUserId: Dispatch<SetStateAction<string | null>>;

  MFAContacts: ContactDTO[];
  setMFAContacts: (
    contacts: ContactDTO[] | ((prev: ContactDTO[]) => ContactDTO[])
  ) => void;
  MFAQuestions: QuestionDTO[];
  setMFAQuestions: Dispatch<SetStateAction<QuestionDTO[]>>;
  clearMFAContacts: () => void;
}

const getInitialMFAContacts = (): ContactDTO[] => {
  try {
    const storedContacts = sessionStorage.getItem(MFA_CONTACTS_STORAGE_KEY);
    if (storedContacts) {
      return JSON.parse(storedContacts);
    }
    return [];
  } catch (error) {
    console.error("Erro ao carregar MFA Contacts do sessionStorage:", error);
    return [];
  }
};

const MFAContext = createContext<MFADTO>({} as MFADTO);

export const MFAContextProvider = ({ children }) => {
  const [attempts, setAttempts] = useState(1);
  const [credentials, setCredentials] = useState<CredentialsDTO>({
    login: sessionStorage.getItem("@UltragazEmpresarial:X-Mfa-Login") || "",
    password: "",
    token: localStorage.getItem("@UltragazEmpresarial:X-Mfa-Hash")
      ? localStorage.getItem("@UltragazEmpresarial:X-Mfa-Hash")
      : "",
  });
  const [userId, setUserId] = useState<string | null>(null);
  const [MFAContacts, setMFAContactsState] = useState<ContactDTO[]>(
    getInitialMFAContacts
  );
  const [MFAQuestions, setMFAQuestions] = useState<QuestionDTO[]>([]);

  const setMFAContacts = (
    contacts: ContactDTO[] | ((prev: ContactDTO[]) => ContactDTO[])
  ) => {
    const newContacts =
      typeof contacts === "function" ? contacts(MFAContacts) : contacts;

    try {
      sessionStorage.setItem(
        MFA_CONTACTS_STORAGE_KEY,
        JSON.stringify(newContacts)
      );
      setMFAContactsState(newContacts);
    } catch (error) {
      console.error("Erro ao salvar MFA Contacts no sessionStorage:", error);
      setMFAContactsState(newContacts);
    }
  };

  const clearMFAContacts = () => {
    sessionStorage.removeItem(MFA_CONTACTS_STORAGE_KEY);
    setMFAContactsState([]);
  };

  return (
    <MFAContext.Provider
      value={useMemo(
        () => ({
          credentials,
          setCredentials,
          setAttempts,
          attempts,
          userId,
          setUserId,
          MFAContacts,
          setMFAContacts,
          MFAQuestions,
          setMFAQuestions,
          clearMFAContacts,
        }),
        [credentials, attempts, MFAContacts, MFAQuestions, userId]
      )}
    >
      {children}
    </MFAContext.Provider>
  );
};

export const useMFAContext = () => useContext(MFAContext);
